import React from 'react';
import OrderQuantityPreview from '../components/OrderQuantityPreview';
import { Link } from 'react-router-dom';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderDto, SupplierOrderItemDto } from '../../../dto/supplierOrder';
import { formatDateTimeEuropean } from '../../../utils/dateUtils';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { formatUnit } from '../../../utils/units';
import {
  buildOriginRows,
  listMissingLines,
  type OriginQuantity,
  type SupplierOrderOriginRow,
} from './supplierOrderOrigin';
import useOriginStockStatus from './useOriginStockStatus';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';

interface SupplierOrderOriginCardProps {
  /** The customer order whose confirmation wrote this draft (migration 197). */
  sourceOrderId: string;
  items: SupplierOrderItemDto[];
  sourceOrder?: SupplierOrderDto['sourceOrder'];
}

const EMPTY = '—';

/**
 * „Skąd wiadomo, co zamówić” (makieta `demand` lane): a ZD written by a
 * customer-order confirmation says where it came from and what it carries — per
 * line the stock, what the customer order still misses and what this ZD brings.
 * Stock figures come from `GET /orders/:id/stock-status` (quantities only, no
 * prices); when that read fails the stock columns show „—”. The link is
 * information only — the ZD is not tied to the customer order, the goods go to
 * the warehouse and are assigned in confirmation order.
 */
export default function SupplierOrderOriginCard({ sourceOrderId, sourceOrder, items }: SupplierOrderOriginCardProps) {
  const { t, i18n } = useTranslation(['orders']);
  const key = (name: string, options?: Record<string, unknown>) => t(`orders:supplierOrders.card.origin.${name}`, options);
  const { status } = useOriginStockStatus(sourceOrderId);

  const formatQuantity = (quantity: OriginQuantity) => (
    `${formatLocalizedQuantity(quantity.value)} ${formatUnit(quantity.unit || 'pcs', i18n.language)}`
  );
  const rows = buildOriginRows(items, status?.lines);
  const missingLines = listMissingLines(status?.lines);
  const noticeDate = status?.supplierDraft.createdAt ? formatDateTimeEuropean(status.supplierDraft.createdAt) : '';

  return (
    <section className="zk-section" data-testid="supplier-order-origin-card">
      <div className="zk-section__header">
        <h3 className="zk-section__title">{key('title')}</h3>
        <span className="zk-section__aside"><Tag>{key('hint')}</Tag></span>
      </div>
      <p className="zk-section__hint">
        {key('lead')}{' '}
        <Link to={`/app/orders/${sourceOrderId}`} data-testid="supplier-order-origin-link">{key('open')}</Link>
      </p>
      {sourceOrder?.id === sourceOrderId && sourceOrder.notes?.trim() ? (
        <div className="zd-origin__notes" data-testid="supplier-order-source-notes">
          <h4>{key('customerNotes', { order: sourceOrder.orderNumber })}</h4>
          <p>{sourceOrder.notes}</p>
        </div>
      ) : null}
      {status ? (
        <p className="zk-section__hint" data-testid="supplier-order-origin-notice">
          <b>{noticeDate ? key('noticeTitle', { date: noticeDate }) : key('noticeTitleNoDate')}</b>{' '}
          {missingLines.length > 0
            ? key('noticeMissing', {
              order: status.orderNumber,
              lines: missingLines.map((line) => `${line.name} ${formatQuantity(line.quantity)}`).join(', '),
            })
            : key('noticeCovered', { order: status.orderNumber })}
        </p>
      ) : null}
      {rows.length > 0 ? <Table<SupplierOrderOriginRow>
        size="small"
        rowKey="itemId"
        pagination={false}
        dataSource={rows}
        scroll={{ x: 520 }}
        columns={[
          {
            title: key('product'),
            key: 'product',
            render: (_value, row) => {
              const dimensions = formatProductDimensions(row.item);
              return (
                <div>
                  <b>{row.item.name}</b>
                  {dimensions ? <div className="zd-cost__sub">{dimensions.toLowerCase()}</div> : null}
                </div>
              );
            },
          },
          {
            title: key('onHand'),
            key: 'onHand',
            align: 'right',
            render: (_value, row) => (row.onHand ? formatQuantity(row.onHand) : EMPTY),
          },
          {
            title: key('missing'),
            key: 'missing',
            align: 'right',
            render: (_value, row) => {
              if (!row.missing) return EMPTY;
              const text = formatQuantity(row.missing);
              return row.missing.value > 0 ? <Tag color="warning">{text}</Tag> : text;
            },
          },
          {
            title: key('inThisOrder'),
            key: 'qty',
            align: 'right',
            render: (_value, row) => <OrderQuantityPreview line={row.item} />,
          },
        ]}
      /> : null}
      <p className="zk-section__hint">{key('note')}</p>
    </section>
  );
}
